/* eslint "jsx-a11y/no-onchange": "off" */
import React, { useState, useContext } from "react";
import { graphql, Link } from "gatsby";
import Img from "gatsby-image";
import { MDXRenderer } from "gatsby-plugin-mdx";
import SEO from "../../components/Seo";
import { getUnit } from "../../helpers/getUnit";
import { currencyFormat, dotFormat } from "../../helpers/formatPrice";
import { StoreContext } from "../../store";
import { useStock } from "../../hooks/useStock";
import useSiteMeta from "../../query/useSiteMeta";
import BreadCrumb from "../../components/Breadcrumb";
import useDefaultPicture from "../../query/useDefaultPicture";
import useCategories from "../../query/useCategories";
import CarouselProducts from "../../components/CarouselProducts";

import styles from "./styles.module.css";

const Product = ({ data, location }) => {
  const { noStock, noStockVariante, siteUrl } = useSiteMeta();
  const { categories: _categories } = useCategories();
  // select form quantity
  const [_quantityOrder, setQuantityOrder] = useState(1);
  const { mdx } = data;
  const { frontmatter, fields, excerpt } = mdx;
  const { slug } = fields;
  const {
    title,
    titleSEO,
    image,
    id,
    price,
    unit,
    quantity,
    onlyShop,
    onlyShopOrLyon,
    withDays,
    withWeekEnd,
    reduceShipping,
    tva,
    variations,
    variationName,
    categories,
  } = frontmatter;
  // find categories
  let cats = null;
  if (_categories && categories) {
    cats = _categories.edges.filter((c) =>
      categories.includes(c.node.fields.relativePath)
    );
  }
  const store = useContext(StoreContext);
  const defaultPicture = useDefaultPicture();

  const {
    customOptions,
    _stock,
    setPrice,
    _option,
    setOption,
    _price,
    _unit,
    setUnit,
    _quantity,
    setQuantity,
  } = useStock({ store, id, price, variations, unit, quantity });

  const meta = {};
  if (onlyShop === true) {
    meta.onlyShop = true;
  }
  if (onlyShopOrLyon === true) {
    meta.onlyShopOrLyon = true;
  }
  if (withDays) {
    meta.withDays = true;
  }
  if (withWeekEnd) {
    meta.withWeekEnd = true;
  }
  if (reduceShipping) {
    meta.reduceShipping = true;
  }
  const { days } = store;
  return (
    <>
      <BreadCrumb location={location} title={title} categories={cats} />
      <SEO title={titleSEO || title} description={mdx.excerpt} />
      <div className={styles.product}>
        <div>
          <div className={styles.product__img}>
            {image ? (
              <Img
                className={styles.img}
                alt={title}
                fluid={image.childImageSharp.fluid}
              />
            ) : (
              <Img
                className={styles.img}
                alt={title}
                fluid={defaultPicture.childImageSharp.fluid}
              />
            )}
          </div>
        </div>
        <div className={styles.product__details}>
          <h1>{title}</h1>
          <span>
            {_stock === 0 ? (
              <span className={styles.nostock}>
                <strong>{customOptions ? noStockVariante : noStock}</strong>
              </span>
            ) : null}
            {_stock !== undefined && _stock < 3 && _stock > 0 ? (
              <span className={styles.limitstock}>
                <strong>
                  {_stock}{" "}
                  {_stock === 1 ? "encore disponible" : "encore disponibles"}
                </strong>
              </span>
            ) : null}
          </span>
          <div className={styles.infos_product}>
            {_quantity && (
              <strong className={styles.product__unity}>
                {_quantity} {getUnit(_unit, _quantity)}
              </strong>
            )}
            <div className={styles.product__price}>
              {currencyFormat(_price)}
            </div>
          </div>
          {withDays === true ? (
            <span className={styles.onlyShop}>
              Uniquement en retrait dans la semaine
            </span>
          ) : null}
          {withWeekEnd === true ? (
            <span className={styles.onlyShop}>
              Uniquement en retrait le samedi ou dimanche
            </span>
          ) : null}
          {onlyShop === true && !withDays && !withWeekEnd ? (
            <span className={styles.onlyShop}>
              Uniquement en retrait, pas de livraison
            </span>
          ) : null}
          {onlyShopOrLyon === true && !onlyShop && !withDays && !withWeekEnd ? (
            <span className={styles.onlyShopOrLyon}>
              Produit uniquement en retrait ou en livraison sur Lyon.
            </span>
          ) : null}
          {!withDays && !withWeekEnd && !onlyShop && !onlyShopOrLyon ? (
            <div className={styles.shippingMode}>
              <Link to="/contact">
                Consultez les modes de livraison disponibles
              </Link>
            </div>
          ) : null}
          <div className={styles.product__actions}>
            {withDays && (
              <div className={styles.withDays}>
                {days.length > 0 ? (
                  <strong>
                    Ce produit est en retrait (obligatoire).
                    <br />
                    Vous pourrez choisir la date juste avant le paiement
                  </strong>
                ) : (
                  <strong className={styles.onlyWeek}>
                    Ce produit est en disponible uniquement en semaine.
                    <br /> Le choix est possible du lundi au vendredi.
                  </strong>
                )}
              </div>
            )}
            <form>
              {customOptions && (
                <label>
                  {variationName}
                  <select
                    onChange={(e) => {
                      const o = variations.find(
                        (v) => v.label === e.target.value
                      );
                      if (o) {
                        // update price and option selected
                        setPrice(o.price ? price + o.price : price);
                        setOption(e.target.value);
                        setUnit(o.unit ? o.unit : null);
                        setQuantity(o.quantity ? o.quantity : null);
                      }
                    }}
                  >
                    {variations.map((n) => {
                      return (
                        <option key={n.label} value={n.label}>
                          {n.label}{" "}
                          {n.price ? `(${currencyFormat(n.price)})` : ""}
                        </option>
                      );
                    })}
                  </select>
                </label>
              )}
              <label>
                Quantité
                <select
                  disabled={_stock === 0 ? true : false}
                  defaultValue={_quantityOrder}
                  onBlur={(e) => setQuantityOrder(e.target.value)}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
                    if (
                      _stock === undefined ||
                      _stock === null ||
                      n <= _stock
                    ) {
                      return (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      );
                    } else {
                      return null;
                    }
                  })}
                </select>
              </label>
              <button
                className="snipcart-add-item"
                data-item-id={id}
                data-item-price={dotFormat(price)}
                data-item-url={slug}
                data-item-taxes={tva || "TVA 5.5"}
                data-item-has-taxes-included="true"
                data-item-description={`${
                  onlyShop === true
                    ? "Produit uniquement en retrait, pas de livraison.|"
                    : ""
                } ${
                  onlyShopOrLyon === true && !onlyShop
                    ? "Produit uniquement en retrait ou en livraison sur Lyon.|"
                    : ""
                } ${excerpt}`}
                data-item-image={
                  image
                    ? `${siteUrl}${image.childImageSharp.fluid.src}`
                    : `${siteUrl}${defaultPicture.childImageSharp.fluid.src}`
                }
                data-item-name={title}
                data-item-quantity={_quantityOrder}
                disabled={_stock === 0 ? true : false}
                data-item-metadata={JSON.stringify(meta)}
                data-item-custom1-name={
                  customOptions
                    ? variationName
                      ? variationName
                      : "Options"
                    : null
                }
                data-item-custom1-options={
                  customOptions ? customOptions.slice(0, -1) : null
                }
                data-item-custom1-value={_option || null}
              >
                Ajouter au panier
              </button>
            </form>
          </div>
        </div>
        <div className={styles.product__description}>
          <h2>Description "{title}"</h2>
          <div>{mdx && <MDXRenderer>{mdx.body}</MDXRenderer>}</div>
        </div>
        {cats && (
          <div className={styles.product__category}>
            {cats.length > 1 ? "Catégories" : "Catégorie"}:{" "}
            {cats.map((c) => (
              <a key={c.node.fields.slug} href={c.node.fields.slug}>
                {c.node.frontmatter.title}
              </a>
            ))}
          </div>
        )}
        {cats && <CarouselProducts categories={cats} current={id} />}
      </div>
    </>
  );
};

export default Product;

export const query = graphql`
  query ProductQuery($slug: String!) {
    mdx(
      frontmatter: { active: { ne: false } }
      fields: { slug: { eq: $slug } }
    ) {
      ...Product
    }
  }
`;
